/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useRetracked } from 'js/lib/retracked';

import { Accordion, AccordionGroup, AccordionHeader, AccordionPanel } from '@coursera/cds-core';

import { LectureItemLink, LectureItemLinkLoading } from 'bundles/clips/components/LectureItemLink';
import type { VLPItem, VLPLesson } from 'bundles/clips/types/vlpSharedTypes';

export type Props = {
  lectureItems: Array<VLPItem>;
  currentVideoItem: VLPItem;
  lessons: Array<VLPLesson>;
  currentLesson?: VLPLesson;
  courseSlug: string;
};

export const CourseVideosAccordionGroup = ({
  lectureItems,
  lessons,
  currentLesson,
  currentVideoItem,
  courseSlug,
}: Props) => {
  const trackEvent = useRetracked();
  const groupLectureItemsByLesson = () => {
    const lessonLecturesMap = lectureItems.reduce((acc, lectureItem) => {
      const belongingLesson = lessons.find((lesson) => lesson.id === lectureItem.lessonId);
      if (belongingLesson) {
        if (!acc[belongingLesson.name]) {
          acc[belongingLesson.name] = [lectureItem];
        } else {
          acc[belongingLesson.name] = [...acc[belongingLesson.name], lectureItem];
        }
      }
      return acc;
    }, {} as { [key: string]: Array<VLPItem> });

    return lessonLecturesMap;
  };

  const lessonLecturesMap = groupLectureItemsByLesson();

  const handleOnChange = (expandedIndices: number[]) => {
    if (expandedIndices.length > 0) {
      const expandedIndex = expandedIndices[0];
      trackEvent({
        trackingData: { lessonName: Object.keys(lessonLecturesMap)[expandedIndex] },
        trackingName: 'lesson_lecture_expand',
        action: 'click',
      });
    } else {
      trackEvent({ trackingData: {}, trackingName: 'lesson_lecture_collapse', action: 'click' });
    }
  };

  return (
    <AccordionGroup
      spacing={12}
      defaultExpanded={[Object.keys(lessonLecturesMap).indexOf(currentLesson?.name ?? '')]}
      data-testid="course-videos-accordion-group"
      onChange={handleOnChange}
    >
      {Object.keys(lessonLecturesMap).map((lessonName) => (
        <Accordion variant="silent" key={lessonName} data-test-id="lesson-accordion">
          <AccordionHeader label={lessonName} />
          <AccordionPanel>
            {lessonLecturesMap[lessonName].map((lectureItem) => (
              <LectureItemLink
                key={lectureItem.id}
                lectureId={lectureItem.id}
                courseSlug={courseSlug}
                lectureName={lectureItem.name}
                lectureTimeCommitment={lectureItem.timeCommitment}
                videoProgressMs={lectureItem.videoProgressMs}
                isSelected={currentVideoItem.id === lectureItem.id}
                trackingName="short_form_content_other_videos_link"
                isCompleted={lectureItem.isPassedOrCompleted}
                data-e2e="next-video-button"
              />
            ))}
          </AccordionPanel>
        </Accordion>
      ))}
    </AccordionGroup>
  );
};

export const CourseVideosAccordionGroupLoading = () => {
  return (
    <div data-testid="course-videos-accordion-group-loading">
      {[...Array(15).keys()].map((n) => (
        <LectureItemLinkLoading key={n} />
      ))}
    </div>
  );
};
